import type {DcaBacktestResult, DcaBacktestTrigger, DcaCondition} from '../../../types/dca';
import {validateCondition} from './dcaConditionUtil';

/** 一天的毫秒数 */
export const DAY_MS = 24 * 60 * 60 * 1000;

/** 周期 → 毫秒（与后端 DcaBacktestService.timeframeMs 一致） */
export const DCA_TF_MS: Record<string, number> = {
    '1m': 60_000,
    '3m': 180_000,
    '5m': 300_000,
    '15m': 900_000,
    '30m': 1_800_000,
    '1H': 3_600_000,
    '2H': 7_200_000,
    '4H': 14_400_000,
    '6H': 21_600_000,
    '12H': 43_200_000,
    '1D': 86_400_000,
};

/**
 * 回测跨度上限（天），与后端周期分桶一致：
 * ≤5m 7 天 / 15m·30m 30 天 / 1H+ 90 天
 */
export const maxRangeDays = (timeframe: string): number => {
    if ('1m' === timeframe || '3m' === timeframe || '5m' === timeframe) {
        return 7;
    }
    if ('15m' === timeframe || '30m' === timeframe) {
        return 30;
    }
    return 90;
};

/** 默认回测区间：结束=当前时间，起点=上限天数之前 */
export const defaultRange = (timeframe: string, nowMs: number = Date.now()): { startMs: number; endMs: number } => {
    const endMs = nowMs;
    return {startMs: endMs - maxRangeDays(timeframe) * DAY_MS, endMs};
};

/** 区间超出当前周期上限时收窄起点（保留结束时间） */
export const clampRangeToTimeframe = (startMs: number, endMs: number, timeframe: string): {
    startMs: number;
    endMs: number
} => {
    const maxMs = maxRangeDays(timeframe) * DAY_MS;
    return endMs - startMs <= maxMs ? {startMs, endMs} : {startMs: endMs - maxMs, endMs};
};

/** 区间跨度（天，向上取整） */
export const spanDays = (startMs: number, endMs: number): number =>
    Math.max(0, Math.ceil((endMs - startMs) / DAY_MS));

/** USDT 金额（$ + 千分位 + 2 位小数） */
export const formatUsdt = (value?: number | null): string => {
    if (null == value || !Number.isFinite(value)) {
        return '--';
    }
    return `$${value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

/** 带符号 USDT（P/L：+$x / -$x） */
export const formatSignedUsdt = (value?: number | null): string => {
    if (null == value || !Number.isFinite(value)) {
        return '--';
    }
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${formatUsdt(Math.abs(value))}`;
};

/** 币量（最多 8 位小数） */
export const formatQuantity = (value?: number | null): string => {
    if (null == value || !Number.isFinite(value)) {
        return '--';
    }
    return value.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 8});
};

/** 百分比（带正号，2 位小数） */
export const formatPercent = (value?: number | null): string => {
    if (null == value || !Number.isFinite(value)) {
        return '--';
    }
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
};

/** 当前价值 = 最新价 × 累计买入量 */
export const currentValueOf = (result?: DcaBacktestResult | null): number | null => {
    if (null == result || null == result.latestPrice || null == result.totalBuyQuantity) {
        return null;
    }
    return result.latestPrice * result.totalBuyQuantity;
};

/** 收益率 = 浮盈亏 / 累计投入 × 100 */
export const pnlPercentOf = (result?: DcaBacktestResult | null): number | null => {
    if (null == result || null == result.unrealizedPnl || null == result.totalBuyAmount || 0 >= result.totalBuyAmount) {
        return null;
    }
    return (result.unrealizedPnl / result.totalBuyAmount) * 100;
};

/** 条件摘要（设置行条件格）：{op} · {n} 条 */
export const buildConditionSummary = (op: 'AND' | 'OR', count: number, t: (key: string) => string): string =>
    t('dca.calc.conditionSummary')
        .replace('{op}', t(`dca.form.op.${op}`))
        .replace('{n}', String(count));

/**
 * 触发点吸附到图表序列最近的K线时间
 * 图表序列为降采样结果，触发K线可能未被采样；按时间距离取最近点，同点去重
 */
export const snapTriggers = (triggers: DcaBacktestTrigger[],
                             points: { time: number }[]): { time: number; price: number }[] => {
    if (0 === triggers.length || 0 === points.length) {
        return [];
    }
    const result: { time: number; price: number }[] = [];
    const seen = new Set<number>();
    triggers.forEach((trigger) => {
        // 二分找首个 ≥ 触发时间的点，再与左侧点比较距离
        let lo = 0;
        let hi = points.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (points[mid].time < trigger.triggerTime) {
                lo = mid + 1;
            } else {
                hi = mid;
            }
        }
        const right = points[lo];
        const left = lo > 0 ? points[lo - 1] : null;
        const time = null != left && trigger.triggerTime - left.time <= right.time - trigger.triggerTime
            ? left.time
            : right.time;
        if (seen.has(time)) {
            return;
        }
        seen.add(time);
        result.push({time, price: trigger.price});
    });
    return result;
};

/** 计算前输入（本地校验与后端规则一致） */
export interface CalcInput {
    symbol?: string;
    amount?: number | null;
    timeframe: string;
    conditions: DcaCondition[];
    lifecycle: 'ONCE' | 'LOOP';
    maxBuyCount?: number | null;
    startMs: number;
    endMs: number;
}

/** 本地校验：返回首条错误文案，null=通过（后端强校验兜底） */
export const validateCalcInputs = (input: CalcInput, t: (key: string) => string): string | null => {
    if (null == input.symbol || '' === input.symbol) {
        return t('dca.form.symbolRequired');
    }
    if (null == input.amount || !(input.amount > 0)) {
        return t('dca.calc.amountRequired');
    }
    if (0 === input.conditions.length) {
        return t('dca.form.conditionEmpty');
    }
    for (const cond of input.conditions) {
        const err = validateCondition(cond, t);
        if (null != err) {
            return err;
        }
    }
    if ('LOOP' === input.lifecycle && (null == input.maxBuyCount || input.maxBuyCount < 1)) {
        return t('dca.form.maxBuyCountRequired');
    }
    if (!(input.startMs < input.endMs)) {
        return t('dca.calc.rangeInvalid');
    }
    if (input.endMs > Date.now()) {
        return t('dca.calc.rangeFuture');
    }
    const maxDays = maxRangeDays(input.timeframe);
    if (spanDays(input.startMs, input.endMs) > maxDays) {
        return t('dca.calc.rangeMaxDays')
            .replace('{tf}', input.timeframe)
            .replace('{days}', String(maxDays));
    }
    return null;
};
